import { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  MessageSquare, Inbox as InboxIcon, Search, User, Users, GitFork, Tag,
  ChevronRight, ChevronDown, ChevronUp, ListFilter, ArrowUpDown, Star,
  Phone, Mail, Trash2, ExternalLink, Plus, Send, Sparkles, Clock,
  FileText, CheckSquare, Edit3, Calendar, File, DollarSign, X,
  RotateCcw, MoreVertical, SlidersHorizontal, Check, RefreshCw, Bot,
  MessageCircle, Smartphone, Share2, HelpCircle, AlertCircle, Paperclip,
  CheckCircle2, TrendingUp, Sliders, Volume2, ShieldCheck, Download
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import { toast } from "sonner";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ManualActionsTab from "./inbox/ManualActionsTab";
import SnippetsTab from "./inbox/SnippetsTab";
import TriggerLinksTab from "./inbox/TriggerLinksTab";
import InboxAnalyticsTab from "./inbox/InboxAnalyticsTab";
import InboxSettingsTab from "./inbox/InboxSettingsTab";

export function getUserMailbox(user) {
  const email = (user?.email || "").toLowerCase().trim();
  const name = user?.name || user?.business_name || (email ? email.split("@")[0] : "Verified Mailbox");

  if (email === "colibrisocialclub@gmail.com") {
    return {
      id: "colibrisocial",
      name: name || "Colibri Social Club",
      email: "colibrisocialclub@gmail.com",
      avatarBg: "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-950 dark:text-purple-300",
      badgeBg: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/70 dark:text-purple-300",
      service: "Primary Google Workspace Mailbox",
      signature: `\n\nBest regards,\n${name}\ncolibrisocialclub@gmail.com`,
      status: "Active & Synced"
    };
  }
  if (email === "contact@amanbiswalthakur.in") {
    return {
      id: "amanbiswal",
      name: name || "Aman Biswal",
      email: "contact@amanbiswalthakur.in",
      avatarBg: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
      badgeBg: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/70 dark:text-blue-300",
      service: "Office Domain Mailbox",
      signature: `\n\nSincerely,\n${name}\ncontact@amanbiswalthakur.in`,
      status: "Active & Synced"
    };
  }
  if (email === "thyzfoto@gmail.com") {
    return {
      id: "thyzfoto",
      name: name || "Thyz Foto",
      email: "thyzfoto@gmail.com",
      avatarBg: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
      badgeBg: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/70 dark:text-amber-300",
      service: "Gmail / Google Workspace",
      signature: `\n\nWarm regards,\n${name}\nthyzfoto@gmail.com`,
      status: "Active & Synced"
    };
  }
  return {
    id: email ? email.replace(/[^a-zA-Z0-9]/g, "_") : "user-mailbox",
    name: name,
    email: email || "user@example.com",
    avatarBg: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
    badgeBg: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/70 dark:text-blue-300",
    service: "Connected & Verified Mailbox",
    signature: `\n\nBest regards,\n${name}\n${email}`,
    status: "Active & Synced"
  };
}

export const CONNECTED_MAILBOXES = [];

// Master Admin reference conversations matching user reference screenshot exactly
const ADMIN_SHOWCASE_CONVERSATIONS = [
  {
    id: "elena-rostova",
    name: "Elena Rostova",
    shortName: "Elena Rostova",
    initials: "ER",
    avatarBg: "bg-amber-200 text-amber-800",
    phone: "+1 (415) 890-3412",
    email: "elena.rostova@prestigegalleria.com",
    firstName: "Elena",
    lastName: "Rostova",
    channel: "email",
    mailbox: "thyzfoto@gmail.com",
    subject: "Luxury Destination Wedding Coverage & Drone Cinematography",
    lastDate: "10:14 AM",
    unreadCount: 1,
    isStarred: true,
    owner: "Thyz Foto",
    followers: 2,
    dnd: false,
    excludeEmailVerification: false,
    tags: ["VIP Wedding", "Destination", "Cinematography"],
    messages: [
      {
        id: "m-er-1",
        sender: "contact",
        senderName: "Elena Rostova",
        senderEmail: "elena.rostova@prestigegalleria.com",
        senderInitials: "ER",
        avatarBg: "bg-amber-200 text-amber-800",
        bubbleType: "inbound",
        text: "Hi Thyz Foto team,\n\nWe adore your drone cinematography portfolio from Lake Como! We are planning a 3-day luxury celebration in Florence this September and want to confirm your availability for multi-cam coverage and highlight films.\n\nCould you please send over your international wedding collection pricing and contract terms?",
        time: "10:14 AM",
        channel: "email",
      },
    ],
  },
  {
    id: "sophia-rossi",
    name: "Sophia Rossi",
    shortName: "Sophia Rossi",
    initials: "SR",
    avatarBg: "bg-blue-200 text-blue-800",
    phone: "+1 (212) 745-9821",
    email: "s.rossi@milanogrowth.io",
    firstName: "Sophia",
    lastName: "Rossi",
    channel: "email",
    mailbox: "contact@amanbiswalthakur.in",
    subject: "Enterprise AI Architecture & CRM Integration Scope",
    lastDate: "Yesterday",
    unreadCount: 1,
    isStarred: true,
    owner: "Aman Biswal",
    followers: 1,
    dnd: false,
    excludeEmailVerification: false,
    tags: ["Enterprise AI", "Consulting", "Architecture"],
    messages: [
      {
        id: "m-sr-1",
        sender: "contact",
        senderName: "Sophia Rossi",
        senderEmail: "s.rossi@milanogrowth.io",
        senderInitials: "SR",
        avatarBg: "bg-blue-200 text-blue-800",
        bubbleType: "inbound",
        text: "Dear Aman,\n\nFollowing up on our discussion regarding the automated multi-channel lead routing engine and custom AI copilot deployment for Milano Growth.\n\nOur executive committee reviewed your technical roadmap and approved the preliminary scope. Could you review the master services agreement so we can schedule the engineering kickoff?",
        time: "03:45 PM",
        channel: "email",
      },
    ],
  },
  {
    id: "julian-thorne",
    name: "Julian Thorne",
    shortName: "Julian Thorne",
    initials: "JT",
    avatarBg: "bg-purple-200 text-purple-800",
    phone: "+1 (310) 902-1845",
    email: "julian@thorneagency.co",
    firstName: "Julian",
    lastName: "Thorne",
    channel: "email",
    mailbox: "colibrisocialclub@gmail.com",
    subject: "Q4 Social Content Retainer & TikTok Production",
    lastDate: "Oct 28",
    unreadCount: 0,
    isStarred: false,
    owner: "Colibri Social",
    followers: 2,
    dnd: false,
    excludeEmailVerification: false,
    tags: ["Social Retainer", "TikTok Ads", "Brand Growth"],
    messages: [
      {
        id: "m-jt-1",
        sender: "contact",
        senderName: "Julian Thorne",
        senderEmail: "julian@thorneagency.co",
        senderInitials: "JT",
        avatarBg: "bg-purple-200 text-purple-800",
        bubbleType: "inbound",
        text: "Hey Colibri Social Club,\n\nThe 15 short-form reels you delivered this month performed extraordinarily well on TikTok (+220k organic views). Let's renew the Q4 retainer and add the 2 weekly carousel batches as discussed.",
        time: "01:20 PM",
        channel: "email",
      },
    ],
  },
  {
    id: "cl-construction",
    name: "C & L Construction",
    shortName: "C & L Constr...",
    initials: "C&",
    avatarBg: "bg-sky-200 text-sky-800",
    phone: "(760) 375-4066",
    email: "contact@clconstruction.com",
    firstName: "C",
    lastName: "& L Construction",
    channel: "sms",
    mailbox: "thyzfoto@gmail.com",
    lastDate: "Sep 25",
    unreadCount: 1,
    isStarred: false,
    owner: "Master Admin",
    followers: 2,
    dnd: false,
    excludeEmailVerification: false,
    tags: ["High Value", "VIP Lead", "Website Refresh"],
    messages: [
      {
        id: "m1",
        sender: "agent",
        senderName: "Agent",
        bubbleType: "outbound-white",
        text: "We can show you over a 10 minutes call. Would you like to connect?\n\nReply YES/NO\nReply STOP to unsubscribe.",
        time: "05:07 AM",
        hasRetry: true,
        channel: "sms",
      },
      {
        id: "d1",
        type: "divider-date",
        date: "Sep 19",
      },
      {
        id: "m2",
        sender: "campaign",
        senderName: "Pragya from RealHUB",
        senderInitials: "US",
        bubbleType: "outbound-blue",
        text: "Hi! It's Pragya from RealHUB! We're helping local businesses with website refreshes for as little as $500! Does your website need a refresher?\nReply STOP to unsubscribe.",
        time: "02:48 PM",
        channel: "sms",
      },
      {
        id: "d2",
        type: "divider-date",
        date: "Sep 25",
      },
      {
        id: "d3",
        type: "divider-new",
        text: "New",
      },
      {
        id: "m3",
        sender: "contact",
        senderName: "C & L Construction",
        senderInitials: "C&",
        avatarBg: "bg-sky-200 text-sky-800",
        bubbleType: "inbound",
        text: "No",
        time: "10:51 PM",
        channel: "sms",
      },
    ],
  },
  {
    id: "oc-home",
    name: "Oc Home Resources",
    shortName: "Oc Home Res...",
    initials: "OH",
    avatarBg: "bg-rose-200 text-rose-800",
    phone: "(949) 555-0182",
    email: "contact@ochomeresources.com",
    firstName: "Oc Home",
    lastName: "Resources",
    channel: "sms",
    mailbox: "thyzfoto@gmail.com",
    lastDate: "Sep 23",
    unreadCount: 1,
    isStarred: false,
    owner: "Sarah Connor (AI SDR)",
    followers: 1,
    dnd: false,
    excludeEmailVerification: false,
    tags: ["Commercial", "Follow Up"],
    messages: [
      {
        id: "m-oc-1",
        sender: "contact",
        senderName: "Oc Home Resources",
        senderInitials: "OH",
        avatarBg: "bg-rose-200 text-rose-800",
        bubbleType: "inbound",
        text: "Can you send the pricing proposal for SEO and local rankings?",
        time: "04:12 PM",
        channel: "sms",
      },
    ],
  },
  {
    id: "omega-structural",
    name: "Omega Structural",
    shortName: "Omega Struct...",
    initials: "OS",
    avatarBg: "bg-purple-200 text-purple-800",
    phone: "(310) 555-0199",
    email: "info@omegaconstruction.com",
    firstName: "Omega",
    lastName: "Structural",
    channel: "sms",
    mailbox: "contact@amanbiswalthakur.in",
    lastDate: "Sep 22",
    unreadCount: 0,
    isStarred: true,
    owner: "Marcus Vance",
    followers: 2,
    dnd: false,
    excludeEmailVerification: false,
    tags: ["Enterprise", "Review Ready"],
    messages: [
      {
        id: "m-om-1",
        sender: "contact",
        senderName: "Omega Structural",
        senderInitials: "OS",
        avatarBg: "bg-purple-200 text-purple-800",
        bubbleType: "inbound",
        text: "Thanks for setting up our new portfolio funnel! Traffic increased 40%.",
        time: "11:30 AM",
        channel: "sms",
      },
    ],
  },
];

export default function Inbox() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isMasterOrAdmin =
    user?.role === "master" ||
    user?.role === "admin" ||
    user?.email === "colibrisocialclub@gmail.com" ||
    user?.email === "thyzfoto@gmail.com" ||
    user?.email === "contact@amanbiswalthakur.in";

  const userMailbox = useMemo(() => getUserMailbox(user), [user]);
  const userEmail = (user?.email || "").toLowerCase().trim();

  // Filter reference conversations strictly to the logged-in user's mailbox
  const userShowcaseConversations = useMemo(() => {
    return ADMIN_SHOWCASE_CONVERSATIONS.filter(
      (c) => (c.mailbox || "").toLowerCase() === userEmail
    );
  }, [userEmail]);

  // Account isolated conversations with multi-mailbox sync
  const [conversations, setConversations] = useState(() => {
    const showcase = ADMIN_SHOWCASE_CONVERSATIONS.filter(
      (c) => (c.mailbox || "").toLowerCase() === userEmail
    );
    if (!user) return showcase;
    const storageKey = `launchdeck_conversations_${user.id || user.email}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Strictly isolate: only keep conversations belonging to this user's mailbox
          const userOnly = parsed.filter(
            (c) => !c.mailbox || c.mailbox.toLowerCase() === userEmail
          );
          const existingIds = new Set(userOnly.map((c) => c.id));
          const missing = showcase.filter((c) => !existingIds.has(c.id));
          return [...userOnly, ...missing];
        }
      } catch {}
    }
    return showcase;
  });

  const [activeId, setActiveId] = useState(() => {
    if (userEmail === "contact@amanbiswalthakur.in") return "sophia-rossi";
    if (userEmail === "colibrisocialclub@gmail.com") return "julian-thorne";
    if (userEmail === "thyzfoto@gmail.com") return "elena-rostova";
    return conversations[0]?.id || null;
  });

  const [mailboxFilter, setMailboxFilter] = useState("all");
  const [selectedSenderEmail, setSelectedSenderEmail] = useState(() => userMailbox.email);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({
    senderEmail: userMailbox.email,
    toEmail: "",
    toName: "",
    subject: "",
    body: ""
  });

  // Ensure active sender and compose sender always match current user's authenticated mailbox
  useEffect(() => {
    if (userMailbox.email) {
      setSelectedSenderEmail(userMailbox.email);
      setComposeForm((prev) => ({ ...prev, senderEmail: userMailbox.email }));
    }
  }, [userMailbox.email]);

  const handleSelectConversation = (id) => {
    setActiveId(id);
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c))
    );
  };

  // Automatically clear unread badge when reading the active conversation
  useEffect(() => {
    if (activeId) {
      setConversations((prev) => {
        const target = prev.find((c) => c.id === activeId);
        if (target && target.unreadCount > 0) {
          return prev.map((c) => (c.id === activeId ? { ...c, unreadCount: 0 } : c));
        }
        return prev;
      });

      const currentConv = conversations.find(c => c.id === activeId);
      if (currentConv?.mailbox) {
        setSelectedSenderEmail(currentConv.mailbox);
        if (currentConv.channel === "email") {
          setChannelMode("email");
        }
      }
    }
  }, [activeId]);

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`launchdeck_conversations_${user.id}`, JSON.stringify(conversations));
    }
  }, [conversations, user]);

  const [subFilter, setSubFilter] = useState("unread");
  const [channelFilter, setChannelFilter] = useState("all");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [channelMode, setChannelMode] = useState("sms");
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const [contactAccordionOpen, setContactAccordionOpen] = useState(true);
  const [contactSubTab, setContactSubTab] = useState("all-fields");
  const [fieldSearch, setFieldSearch] = useState("");
  const [activeSideTool, setActiveSideTool] = useState("contact");
  const [aiDraftBusy, setAiDraftBusy] = useState(false);
  const [contactDetailsOpen, setContactDetailsOpen] = useState(true);
  const [sortAsc, setSortAsc] = useState(false);

  // Tag editor inline state
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagText, setNewTagText] = useState("");

  // Notes state
  const [notes, setNotes] = useState({
    "cl-construction": [
      { id: "n1", text: "Client interested in $500 website refresh. Follow up regarding mobile speed.", author: "Master Admin", time: "Sep 25, 10:55 PM" },
      { id: "n2", text: "Requested quote for Google Review Automation add-on.", author: "Sarah Connor", time: "Sep 24, 02:10 PM" }
    ],
    "oc-home": [
      { id: "n3", text: "Needs SEO proposal delivered by end of week.", author: "Marcus Vance", time: "Sep 23, 04:30 PM" }
    ]
  });
  const [newNoteInput, setNewNoteInput] = useState("");

  // Tasks state
  const [tasks, setTasks] = useState({
    "cl-construction": [
      { id: "t1", title: "Send website refresh preview link", dueDate: "Tomorrow", completed: false },
      { id: "t2", title: "Verify DNS records for custom domain", dueDate: "Oct 2", completed: true },
      { id: "t3", title: "Schedule 10-minute discovery call", dueDate: "Friday", completed: false }
    ],
    "oc-home": [
      { id: "t4", title: "Draft SEO keyword strategy deck", dueDate: "Oct 1", completed: false }
    ]
  });
  const [newTaskInput, setNewTaskInput] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("Tomorrow");

  // Workflows state
  const [workflows, setWorkflows] = useState({
    "cl-construction": [
      { id: "w1", name: "AI Speed-to-Lead SMS", desc: "Instant AI reply within 60 seconds", active: true },
      { id: "w2", name: "5-Star Review Request Blast", desc: "Nudge customer after service complete", active: true },
      { id: "w3", name: "5-Day Cold Outreach Sequence", desc: "Automated multi-channel cadence", active: false }
    ],
    "oc-home": [
      { id: "w1", name: "AI Speed-to-Lead SMS", desc: "Instant AI reply within 60 seconds", active: true },
      { id: "w2", name: "5-Star Review Request Blast", desc: "Nudge customer after service complete", active: false }
    ]
  });

  // Calendar booking state
  const [meetingForm, setMeetingForm] = useState({
    title: "15-Minute Strategy & Discovery Call",
    date: "Tomorrow",
    time: "02:00 PM",
    channel: "Google Meet"
  });

  // Payment Link Generator state
  const [paymentForm, setPaymentForm] = useState({
    item: "Website Redesign & AI Setup Retainer",
    amount: "500.00",
    currency: "USD"
  });

  // Phone type state
  const [phoneType, setPhoneType] = useState("Mobile");

  const activeConversation = conversations.find(c => c.id === activeId) || conversations[0];
  const [contactEdit, setContactEdit] = useState({
    firstName: activeConversation?.firstName || "",
    lastName: activeConversation?.lastName || "",
    email: activeConversation?.email || "",
    phone: activeConversation?.phone || "",
    excludeEmailVerification: activeConversation?.excludeEmailVerification || false,
    dnd: activeConversation?.dnd || false,
  });

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (activeConversation) {
      setContactEdit({
        firstName: activeConversation.firstName || "",
        lastName: activeConversation.lastName || "",
        email: activeConversation.email || "",
        phone: activeConversation.phone || "",
        excludeEmailVerification: activeConversation.excludeEmailVerification || false,
        dnd: activeConversation.dnd || false,
      });
    }
  }, [activeId]);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(conversations.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelectCard = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleToggleStar = (id, e) => {
    e.stopPropagation();
    setConversations(prev =>
      prev.map(c => c.id === id ? { ...c, isStarred: !c.isStarred } : c)
    );
    toast.success("Starred status updated");
  };

  const handleSendMessage = async () => {
    if (!replyText.trim()) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const activeSender = userMailbox;
    const textToSend = replyText.trim();
    const targetEmail = activeConversation?.email || "";

    setReplyText("");

    if (channelMode === "email" && targetEmail) {
      toast.info(`Dispatching live email to ${targetEmail} via ${activeSender.name}...`);
      try {
        const { data } = await api.post("/inbox/send-email", {
          to_email: targetEmail,
          to_name: activeConversation?.name || targetEmail,
          subject: activeConversation?.subject || `Follow-up from ${activeSender.name}`,
          body: textToSend,
          from_email: activeSender.email,
          from_name: activeSender.name
        });
        toast.success(`Email live delivered to ${targetEmail} via ${data.provider?.toUpperCase() || 'SMTP'}!`);
      } catch (err) {
        toast.error(`Email dispatch warning: ${err?.response?.data?.detail || err.message}`);
      }
    } else {
      toast.success(`Message sent via ${channelMode.toUpperCase()}!`);
    }

    const newMessage = {
      id: "msg-" + Date.now(),
      sender: "agent",
      senderName: channelMode === "email" ? activeSender.name : (user?.name || "You"),
      senderEmail: channelMode === "email" ? activeSender.email : user?.email,
      bubbleType: "outbound-white",
      text: textToSend,
      time: timeStr,
      channel: channelMode,
      mailbox: activeSender.email
    };

    setConversations(prev =>
      prev.map(c => {
        if (c.id === activeId) {
          return {
            ...c,
            unreadCount: 0,
            messages: [...c.messages, newMessage],
          };
        }
        return c;
      })
    );

    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSendComposeEmail = async (e) => {
    e.preventDefault();
    if (!composeForm.toEmail || !composeForm.subject || !composeForm.body) {
      toast.error("Please provide recipient email, subject, and message body.");
      return;
    }
    const senderObj = userMailbox;
    const fullBodyWithSig = composeForm.body + senderObj.signature;

    toast.info(`Dispatching live email to ${composeForm.toEmail}...`);
    try {
      const { data } = await api.post("/inbox/send-email", {
        to_email: composeForm.toEmail,
        to_name: composeForm.toName,
        subject: composeForm.subject,
        body: fullBodyWithSig,
        from_email: senderObj.email,
        from_name: senderObj.name
      });
      toast.success(`Email live delivered to ${composeForm.toEmail} via ${data.provider?.toUpperCase() || 'SMTP'}!`);
    } catch (err) {
      toast.error(`Email dispatch error: ${err?.response?.data?.detail || err.message}`);
    }

    const newConvId = "email-" + Date.now();
    const initials = (composeForm.toName || composeForm.toEmail.split("@")[0])
      .split(" ")
      .map(p => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "CL";

    const newConversation = {
      id: newConvId,
      name: composeForm.toName || composeForm.toEmail,
      shortName: composeForm.toName || composeForm.toEmail,
      initials,
      avatarBg: "bg-blue-100 text-blue-800",
      phone: "",
      email: composeForm.toEmail,
      firstName: (composeForm.toName || "").split(" ")[0] || "Client",
      lastName: (composeForm.toName || "").split(" ").slice(1).join(" ") || "",
      channel: "email",
      mailbox: senderObj.email,
      subject: composeForm.subject,
      lastDate: "Just now",
      unreadCount: 0,
      isStarred: false,
      owner: senderObj.name,
      followers: 1,
      dnd: false,
      excludeEmailVerification: false,
      tags: ["Direct Outbound", "Email"],
      messages: [
        {
          id: "m-out-" + Date.now(),
          sender: "agent",
          senderName: senderObj.name,
          senderEmail: senderObj.email,
          bubbleType: "outbound-white",
          text: fullBodyWithSig,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          channel: "email",
        }
      ]
    };

    setConversations(prev => [newConversation, ...prev]);
    setActiveId(newConvId);
    setSelectedSenderEmail(senderObj.email);
    setChannelMode("email");
    setComposeOpen(false);
    setComposeForm({
      senderEmail: senderObj.email,
      toEmail: "",
      toName: "",
      subject: "",
      body: ""
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDraftAI = async () => {
    setAiDraftBusy(true);
    const lastInbound = activeConversation?.messages?.filter(m => m.sender === "contact").slice(-1)[0];
    const contextText = lastInbound ? lastInbound.text : "No previous question";

    setTimeout(() => {
      if (contextText.toLowerCase().includes("no") || contextText.toLowerCase().includes("stop")) {
        setReplyText("Understood! We have updated your communication preferences. If you ever need assistance with website design or reviews, feel free to reach out anytime!");
      } else if (contextText.toLowerCase().includes("pricing") || contextText.toLowerCase().includes("proposal")) {
        setReplyText("Hi! We offer complete website refresh packages starting at just $500, with optional AI review automation. Would you be open to a quick 10-minute walk-through tomorrow at 2:00 PM?");
      } else {
        setReplyText("Thanks for reaching out! We'd love to help you scale your business. Would Thursday at 2:00 PM work for a quick 10-minute discovery call?");
      }
      setAiDraftBusy(false);
      toast.success("AI draft generated via Gemini!");
    }, 400);
  };

  const handleSaveContact = () => {
    setConversations(prev =>
      prev.map(c => {
        if (c.id === activeId) {
          return {
            ...c,
            firstName: contactEdit.firstName,
            lastName: contactEdit.lastName,
            name: `${contactEdit.firstName} ${contactEdit.lastName}`.trim(),
            shortName: `${contactEdit.firstName} ${contactEdit.lastName}`.slice(0, 14) + "...",
            phone: contactEdit.phone,
            email: contactEdit.email,
            dnd: contactEdit.dnd,
            excludeEmailVerification: contactEdit.excludeEmailVerification,
          };
        }
        return c;
      })
    );
    toast.success("Contact details updated successfully");
  };

  // Reassign owner
  const handleAssignOwner = (newOwner) => {
    setConversations(prev =>
      prev.map(c => c.id === activeId ? { ...c, owner: newOwner } : c)
    );
    toast.success(`Assigned to ${newOwner}`);
  };

  // Add tag
  const handleAddTag = (e) => {
    e.preventDefault();
    if (!newTagText.trim()) return;
    const tag = newTagText.trim();
    setConversations(prev =>
      prev.map(c => {
        if (c.id === activeId) {
          const currentTags = c.tags || [];
          if (!currentTags.includes(tag)) {
            return { ...c, tags: [...currentTags, tag] };
          }
        }
        return c;
      })
    );
    setNewTagText("");
    setIsAddingTag(false);
    toast.success(`Tag "${tag}" added`);
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove) => {
    setConversations(prev =>
      prev.map(c => {
        if (c.id === activeId) {
          return { ...c, tags: (c.tags || []).filter(t => t !== tagToRemove) };
        }
        return c;
      })
    );
    toast.success(`Tag "${tagToRemove}" removed`);
  };

  // Notes handlers
  const handleAddNote = (e) => {
    e.preventDefault();
    if (!newNoteInput.trim() || !activeId) return;
    const now = new Date();
    const timeStr = now.toLocaleDateString([], { month: "short", day: "numeric" }) + ", " + now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const noteObj = {
      id: "n-" + Date.now(),
      text: newNoteInput.trim(),
      author: user?.name || "Master Admin",
      time: timeStr
    };
    setNotes(prev => ({
      ...prev,
      [activeId]: [noteObj, ...(prev[activeId] || [])]
    }));
    setNewNoteInput("");
    toast.success("Note added successfully");
  };

  const handleDeleteNote = (noteId) => {
    setNotes(prev => ({
      ...prev,
      [activeId]: (prev[activeId] || []).filter(n => n.id !== noteId)
    }));
    toast.success("Note deleted");
  };

  // Tasks handlers
  const handleAddTask = (e) => {
    e.preventDefault();
    if (!newTaskInput.trim() || !activeId) return;
    const taskObj = {
      id: "t-" + Date.now(),
      title: newTaskInput.trim(),
      dueDate: newTaskDueDate || "Tomorrow",
      completed: false
    };
    setTasks(prev => ({
      ...prev,
      [activeId]: [taskObj, ...(prev[activeId] || [])]
    }));
    setNewTaskInput("");
    toast.success("Task added to checklist");
  };

  const handleToggleTask = (taskId) => {
    setTasks(prev => ({
      ...prev,
      [activeId]: (prev[activeId] || []).map(t =>
        t.id === taskId ? { ...t, completed: !t.completed } : t
      )
    }));
  };

  // Workflows toggle handler
  const handleToggleWorkflow = (wfId) => {
    setWorkflows(prev => ({
      ...prev,
      [activeId]: (prev[activeId] || []).map(w =>
        w.id === wfId ? { ...w, active: !w.active } : w
      )
    }));
    toast.success("Workflow enrollment updated");
  };

  // Send Payment Link Card directly into Conversation
  const handleSendPaymentLink = (e) => {
    e.preventDefault();
    if (!activeId) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const payMessage = {
      id: "pay-" + Date.now(),
      sender: "agent",
      senderName: "You",
      bubbleType: "payment-card",
      item: paymentForm.item,
      amount: paymentForm.amount,
      currency: paymentForm.currency,
      payUrl: `https://deck.weddingsthyzfoto.com/checkout?item=${encodeURIComponent(paymentForm.item)}&amt=${paymentForm.amount}`,
      time: timeStr,
      channel: channelMode
    };
    setConversations(prev =>
      prev.map(c => c.id === activeId ? { ...c, messages: [...c.messages, payMessage] } : c)
    );
    toast.success(`Payment link for $${paymentForm.amount} sent to client!`);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Send Calendar Invite Card directly into Conversation
  const handleSendMeetingInvite = (e) => {
    e.preventDefault();
    if (!activeId) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const meetingMessage = {
      id: "meet-" + Date.now(),
      sender: "agent",
      senderName: "You",
      bubbleType: "calendar-card",
      title: meetingForm.title,
      date: meetingForm.date,
      time: meetingForm.time,
      channel: meetingForm.channel,
      joinUrl: "https://meet.google.com/col-mgmt-rev",
      timeSent: timeStr
    };
    setConversations(prev =>
      prev.map(c => c.id === activeId ? { ...c, messages: [...c.messages, meetingMessage] } : c)
    );
    toast.success("Calendar meeting invitation sent!");
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Send Document Card directly into Conversation
  const handleSendDocument = (docName) => {
    if (!activeId) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const docMessage = {
      id: "doc-" + Date.now(),
      sender: "agent",
      senderName: "You",
      bubbleType: "doc-card",
      fileName: docName,
      fileSize: "2.4 MB",
      docUrl: "#",
      time: timeStr
    };
    setConversations(prev =>
      prev.map(c => c.id === activeId ? { ...c, messages: [...c.messages, docMessage] } : c)
    );
    toast.success(`Document "${docName}" shared in conversation!`);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Filter conversations with multi-mailbox support
  let filteredConversations = conversations.filter(c => {
    if (channelFilter !== "all" && c.channel !== channelFilter) return false;
    if (mailboxFilter !== "all" && c.mailbox && c.mailbox !== mailboxFilter) return false;
    if (subFilter === "unread") return c.unreadCount > 0;
    if (subFilter === "starred") return c.isStarred;
    return true;
  });

  if (sortAsc) {
    filteredConversations = [...filteredConversations].reverse();
  }

  const activeNotes = (activeId && notes[activeId]) || [];
  const activeTasks = (activeId && tasks[activeId]) || [];
  const activeWorkflows = (activeId && workflows[activeId]) || [
    { id: "w1", name: "AI Speed-to-Lead SMS", desc: "Instant AI reply within 60 seconds", active: true },
    { id: "w2", name: "5-Star Review Request Blast", desc: "Nudge customer after service complete", active: false }
  ];

  const activeTab = searchParams.get("tab") || "conversations";

  if (activeTab === "manual-actions") {
    return <ManualActionsTab />;
  }
  if (activeTab === "snippets") {
    return <SnippetsTab />;
  }
  if (activeTab === "trigger-links") {
    return <TriggerLinksTab />;
  }
  if (activeTab === "analytics") {
    return <InboxAnalyticsTab />;
  }
  if (activeTab === "settings") {
    return <InboxSettingsTab />;
  }

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-slate-50 dark:bg-slate-950 font-sans" data-testid="inbox-page">
      {/* ─────────────────────────────────────────────────────────────
          COLUMN 1: NARROW ICON RAIL (CHANNELS) (~48px)
      ───────────────────────────────────────────────────────────── */}
      <aside className="w-12 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col items-center py-4 gap-4 shrink-0 select-none">
        <button
          onClick={() => { setChannelFilter("all"); toast.success("Filter: All channels"); }}
          title="All Inboxes"
          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
            channelFilter === "all" ? "bg-blue-600 text-white shadow-xs" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          <InboxIcon className="w-4 h-4" />
        </button>

        <button
          onClick={() => { setChannelFilter("sms"); toast.success("Filter: SMS conversations"); }}
          title="SMS Channel"
          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
            channelFilter === "sms" ? "bg-blue-600 text-white shadow-xs" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          <Smartphone className="w-4 h-4" />
        </button>

        <button
          onClick={() => { setChannelFilter("email"); toast.success("Filter: Email threads"); }}
          title="Email Channel"
          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
            channelFilter === "email" ? "bg-blue-600 text-white shadow-xs" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          <Mail className="w-4 h-4" />
        </button>

        <button
          onClick={() => { setChannelFilter("whatsapp"); toast.success("Filter: WhatsApp messages"); }}
          title="WhatsApp Channel"
          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
            channelFilter === "whatsapp" ? "bg-blue-600 text-white shadow-xs" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          <MessageCircle className="w-4 h-4" />
        </button>

        <div className="w-6 h-px bg-slate-200 dark:bg-slate-800 my-1" />

        <button
          onClick={() => toast.success("All messaging carrier endpoints operational")}
          title="Carrier Status"
          className="w-9 h-9 rounded-lg flex items-center justify-center text-emerald-500 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <Check className="w-4 h-4" />
        </button>
      </aside>

      {/* ─────────────────────────────────────────────────────────────
          COLUMN 2: CONVERSATION LIST COLUMN (~320px)
      ───────────────────────────────────────────────────────────── */}
      <section className="w-[320px] min-w-[320px] max-w-[320px] border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col shrink-0 select-none overflow-hidden">
        {/* User Authenticated Mailbox Header */}
        <div className="p-2.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/90 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-blue-600" />
              Connected Mailbox
            </span>
            <Button
              size="sm"
              onClick={() => {
                setComposeForm(prev => ({
                  ...prev,
                  senderEmail: userMailbox.email
                }));
                setComposeOpen(true);
              }}
              className="h-6 px-2 text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 text-white gap-1 shadow-xs"
            >
              <Plus className="w-3 h-3" /> Compose
            </Button>
          </div>

          {/* User Mailbox Switcher Chips */}
          <div className="flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-none">
            <button
              onClick={() => { setMailboxFilter("all"); toast.success("Showing all inbox messages"); }}
              className={`px-2 py-1 text-[10px] rounded-md font-semibold shrink-0 transition-colors ${
                mailboxFilter === "all"
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100"
              }`}
            >
              All Inboxes ({conversations.length})
            </button>
            <button
              onClick={() => { setMailboxFilter(userMailbox.email); toast.success(`Active Mailbox: ${userMailbox.name}`); }}
              className={`px-2 py-1 text-[10px] rounded-md font-semibold shrink-0 transition-colors flex items-center gap-1 ${
                mailboxFilter === userMailbox.email
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100"
              }`}
              title={userMailbox.email}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse" />
              {userMailbox.name} ({conversations.filter(c => !c.mailbox || c.mailbox.toLowerCase() === userMailbox.email.toLowerCase()).length})
            </button>
          </div>
        </div>

        {/* Top Header: Unread Dropdown, Sort, Filter */}
        <div className="p-3 border-b border-slate-200 dark:border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            {/* Unread / All Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100 hover:text-blue-600">
                  <span className="capitalize">{subFilter}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-36">
                <DropdownMenuItem onClick={() => setSubFilter("unread")}>Unread Only</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubFilter("all")}>All Messages</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSubFilter("starred")}>Starred Only</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Quick Action Icons: Sort & Filter */}
            <div className="flex items-center gap-1 text-slate-400">
              <button
                onClick={() => {
                  setSortAsc(!sortAsc);
                  toast.success(sortAsc ? "Sorted newest first" : "Sorted oldest first");
                }}
                className="p-1 rounded hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="Sort Conversations"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  const nextF = subFilter === "all" ? "unread" : subFilter === "unread" ? "starred" : "all";
                  setSubFilter(nextF);
                  toast.success(`Filter switched to ${nextF}`);
                }}
                className="p-1 rounded hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="Toggle filter"
              >
                <ListFilter className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Search Box */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 placeholder:text-slate-400 outline-none focus:border-blue-500"
            />
          </div>

          {/* Select All Checkbox Row */}
          <div className="flex items-center justify-between pt-1 text-[11px] text-slate-500">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
              />
              <span>Select All</span>
            </label>
            <span>{filteredConversations.length} total</span>
          </div>
        </div>

        {/* Conversation Cards Scroll List */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400">
              No conversations in {subFilter}
            </div>
          ) : (
            filteredConversations.map((c) => {
              const isSelected = selectedIds.includes(c.id);
              const isActive = activeId === c.id;
              const lastMsg = c.messages[c.messages.length - 1];

              return (
                <div
                  key={c.id}
                  onClick={() => handleSelectConversation(c.id)}
                  className={`p-3 cursor-pointer transition-colors relative flex items-start gap-2.5 ${
                    isActive
                      ? "bg-blue-50/70 dark:bg-blue-950/30 border-l-3 border-blue-600"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  {/* Select Card Checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleToggleSelectCard(c.id, e)}
                    className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 shrink-0"
                  />

                  {/* Avatar Circle */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${c.avatarBg}`}>
                    {c.initials}
                  </div>

                  {/* Body Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-semibold text-xs text-slate-900 dark:text-slate-100 truncate">
                        {c.name}
                      </span>
                      <span className="text-[10px] text-slate-400 shrink-0 ml-1">
                        {c.lastDate}
                      </span>
                    </div>

                    {c.subject && (
                      <div className="text-[10px] font-semibold text-slate-800 dark:text-slate-200 truncate mb-0.5">
                        {c.subject}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate pr-1">
                        {lastMsg ? (lastMsg.text || "New message") : "No messages yet"}
                      </p>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {/* Star Toggle */}
                        <button
                          onClick={(e) => handleToggleStar(c.id, e)}
                          className="text-slate-300 hover:text-amber-400"
                        >
                          <Star className={`w-3.5 h-3.5 ${c.isStarred ? "text-amber-400 fill-amber-400" : ""}`} />
                        </button>

                        {/* Unread Counter Badge */}
                        {c.unreadCount > 0 && (
                          <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold flex items-center justify-center">
                            {c.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>

                    {c.mailbox && (
                      <div className="mt-1 flex items-center gap-1 text-[9px] font-medium text-slate-500 dark:text-slate-400">
                        <Mail className="w-2.5 h-2.5 text-blue-500" />
                        <span>Via {c.mailbox}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          COLUMN 3: MAIN CHAT STREAM & REPLY BOX (FLEX-1)
      ───────────────────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col bg-slate-100/50 dark:bg-slate-950 relative overflow-hidden">
        {!activeConversation ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-slate-400">
            <MessageSquare className="w-12 h-12 text-slate-300 mb-3" />
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">No conversation selected</h3>
            <p className="text-xs text-slate-400 mt-1 max-w-sm">Choose a thread from the left or share your booking link to receive inbound client requests.</p>
          </div>
        ) : (
          <>
            {/* Contact Chat Header */}
            <div className="h-13 px-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${activeConversation.avatarBg}`}>
                  {activeConversation.initials}
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-900 dark:text-white block">
                    {activeConversation.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">
                      {activeConversation.phone || activeConversation.email} · Assigned to {activeConversation.owner || "Unassigned"}
                    </span>
                    {activeConversation.mailbox && (
                      <Badge variant="outline" className="text-[9px] h-4 px-1.5 font-medium border-blue-200 text-blue-600 dark:border-blue-800 dark:text-blue-400">
                        <Mail className="w-2.5 h-2.5 mr-1" />
                        {activeConversation.mailbox}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Header Right Action Tools */}
              <div className="flex items-center gap-2 text-slate-500">
                {/* Reassign Owner Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 transition-colors"
                      title="Reassign Agent"
                    >
                      <User className="w-3.5 h-3.5 text-blue-600" />
                      <span className="font-medium text-[11px]">{activeConversation.owner || "Unassigned"}</span>
                      <ChevronDown className="w-3 h-3 text-slate-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => handleAssignOwner("Unassigned")}>Unassigned</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAssignOwner("Master Admin")}>Master Admin (You)</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAssignOwner("Sarah Connor (AI SDR)")}>Sarah Connor (AI SDR)</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAssignOwner("Marcus Vance")}>Marcus Vance (Closer)</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Call Outbound */}
                <button
                  onClick={() => toast.success(`Calling ${activeConversation.name} at ${activeConversation.phone}...`)}
                  title={`Call ${activeConversation.phone}`}
                  className="p-1.5 rounded hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                </button>

                {/* Star */}
                <button
                  onClick={(e) => handleToggleStar(activeConversation.id, e)}
                  className="p-1.5 rounded hover:text-amber-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  title="Star conversation"
                >
                  <Star className={`w-4 h-4 ${activeConversation.isStarred ? "text-amber-400 fill-amber-400" : ""}`} />
                </button>

                {/* Switch to Email */}
                <button
                  onClick={() => {
                    setChannelMode("email");
                    toast.success("Switched channel to Email");
                  }}
                  title="Send Email"
                  className="p-1.5 rounded hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                </button>

                {/* Archive / Delete */}
                <button
                  onClick={() => {
                    setConversations(prev => prev.filter(c => c.id !== activeConversation.id));
                    toast.success("Conversation archived");
                  }}
                  title="Archive conversation"
                  className="p-1.5 rounded hover:text-red-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Message Stream */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {activeConversation.messages.map((item) => {
                if (item.type === "divider-date") {
                  return (
                    <div key={item.id} className="flex justify-center my-3">
                      <span className="px-3 py-0.5 rounded-full bg-slate-200/80 dark:bg-slate-800 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                        {item.date}
                      </span>
                    </div>
                  );
                }

                if (item.type === "divider-new") {
                  return (
                    <div key={item.id} className="flex items-center gap-3 my-3">
                      <div className="h-px bg-red-200 dark:bg-red-900/60 flex-1" />
                      <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">
                        New Messages
                      </span>
                      <div className="h-px bg-red-200 dark:bg-red-900/60 flex-1" />
                    </div>
                  );
                }

                // Inbound message bubble
                if (item.bubbleType === "inbound") {
                  return (
                    <div key={item.id} className="flex items-start gap-2.5 max-w-xl">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px] shrink-0 mt-1 ${item.avatarBg || "bg-sky-200 text-sky-800"}`}>
                        {item.senderInitials || "C"}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                            {item.senderName}
                          </span>
                          <span className="text-[10px] text-slate-400">{item.time}</span>
                        </div>
                        <div className="p-3.5 rounded-2xl rounded-tl-xs bg-white dark:bg-slate-800 text-xs text-slate-800 dark:text-slate-100 shadow-xs border border-slate-200/60 dark:border-slate-700 whitespace-pre-wrap leading-relaxed">
                          {item.text}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Payment Card Bubble
                if (item.bubbleType === "payment-card") {
                  return (
                    <div key={item.id} className="flex flex-col items-end my-2">
                      <div className="max-w-md w-full p-4 rounded-2xl rounded-tr-xs bg-gradient-to-br from-blue-900 to-indigo-950 text-white shadow-md border border-blue-800 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-emerald-400" />
                            <span className="text-xs font-bold uppercase tracking-wider text-blue-200">Payment Request</span>
                          </div>
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                            Awaiting Payment
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm">{item.item}</div>
                        <div className="text-2xl font-black text-white">${item.amount} <span className="text-xs font-normal text-blue-300">USD</span></div>
                        <div className="pt-2 flex items-center justify-between border-t border-blue-800/80">
                          <a
                            href={item.payUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold shadow-xs transition-colors"
                          >
                            Pay Invoice Online →
                          </a>
                          <span className="text-[10px] text-blue-300">{item.time}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Calendar Meeting Card Bubble
                if (item.bubbleType === "calendar-card") {
                  return (
                    <div key={item.id} className="flex flex-col items-end my-2">
                      <div className="max-w-md w-full p-4 rounded-2xl rounded-tr-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md space-y-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-blue-600">
                            <Calendar className="w-4 h-4" />
                            <span className="text-xs font-bold uppercase tracking-wider">Scheduled Meeting</span>
                          </div>
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                            Confirmed
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm text-slate-900 dark:text-white">{item.title}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 font-sans">
                          📅 {item.date} at {item.time} ({item.channel})
                        </div>
                        <div className="pt-2 flex items-center justify-between border-t border-slate-100 dark:border-slate-700">
                          <a
                            href={item.joinUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs"
                          >
                            Open Meeting Room
                          </a>
                          <span className="text-[10px] text-slate-400">{item.timeSent}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Document Card Bubble
                if (item.bubbleType === "doc-card") {
                  return (
                    <div key={item.id} className="flex flex-col items-end my-2">
                      <div className="max-w-sm w-full p-3.5 rounded-2xl rounded-tr-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xs flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white truncate">{item.fileName}</div>
                            <div className="text-[10px] text-slate-400">{item.fileSize} · PDF Document</div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toast.success(`Opening ${item.fileName}...`)}
                          className="h-7 text-xs text-blue-600 font-medium"
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  );
                }

                // Standard outbound bubble
                return (
                  <div key={item.id} className="flex flex-col items-end">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] text-slate-400">{item.time}</span>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                        {item.senderName} {item.senderEmail ? `(${item.senderEmail})` : ""}
                      </span>
                    </div>
                    <div
                      className={`p-3.5 rounded-2xl rounded-tr-xs max-w-xl text-xs leading-relaxed shadow-xs whitespace-pre-wrap ${
                        item.bubbleType === "outbound-blue"
                          ? "bg-blue-600 text-white"
                          : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200/60 dark:border-slate-700"
                      }`}
                    >
                      {item.text}
                    </div>
                    {item.hasRetry && (
                      <button
                        onClick={() => toast.success("Message re-sent successfully via carrier!")}
                        className="flex items-center gap-1 text-[10px] text-red-500 hover:text-red-600 font-medium mt-1 cursor-pointer"
                      >
                        <RotateCcw className="w-2.5 h-2.5" /> Delivery retry
                      </button>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Bottom Composer / Reply Box */}
            <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 space-y-2">
              <div className="flex items-center justify-between">
                {/* Channel Selector Pill */}
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg text-xs">
                  {["sms", "email", "whatsapp"].map(ch => (
                    <button
                      key={ch}
                      onClick={() => setChannelMode(ch)}
                      className={`px-2.5 py-1 rounded-md capitalize font-semibold transition-all ${
                        channelMode === ch
                          ? "bg-white dark:bg-slate-900 text-blue-600 shadow-xs"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>

                {/* AI Assistant Quick Draft Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDraftAI}
                  disabled={aiDraftBusy}
                  className="gap-1.5 h-7 text-xs text-purple-600 border-purple-200 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {aiDraftBusy ? "Drafting..." : "AI Suggest Reply"}
                </Button>
              </div>

              {/* Authenticated From: Sender Identity (when in Email mode) */}
              {channelMode === "email" && (
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-500 text-[11px]">From Mailbox:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-100 text-xs">
                      {userMailbox.name} &lt;{userMailbox.email}&gt;
                    </span>
                  </div>
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                    {userMailbox.service}
                  </span>
                </div>
              )}

              {/* Textarea */}
              <div className="relative">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Type a message via ${channelMode.toUpperCase()}... (Press Enter to send, Shift+Enter for new line)`}
                  rows={2}
                  className="w-full pr-24 resize-none text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-blue-500 rounded-xl"
                />

                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  <Button
                    size="sm"
                    onClick={handleSendMessage}
                    disabled={!replyText.trim()}
                    className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs gap-1 shadow-xs"
                  >
                    <Send className="w-3 h-3" /> Send
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* ─────────────────────────────────────────────────────────────
          COLUMN 4: CONTACT DETAILS & RIGHT QUICK-ACTION STRIP (~360px)
      ───────────────────────────────────────────────────────────── */}
      {contactDetailsOpen && (
        <section className="w-[340px] min-w-[340px] max-w-[340px] border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex shrink-0 overflow-hidden">
          {/* Main Body depending on activeSideTool */}
          <div className="flex-1 p-3.5 overflow-y-auto space-y-3.5">
            {/* Header: Title and Close */}
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-xs font-bold text-slate-800 dark:text-slate-100 capitalize flex items-center gap-1.5">
                {activeSideTool === "contact" && <><User className="w-3.5 h-3.5 text-blue-600" /> Contact Details</>}
                {activeSideTool === "notes" && <><FileText className="w-3.5 h-3.5 text-blue-600" /> Contact Notes ({activeNotes.length})</>}
                {activeSideTool === "tasks" && <><CheckSquare className="w-3.5 h-3.5 text-blue-600" /> Action Tasks ({activeTasks.length})</>}
                {activeSideTool === "workflows" && <><GitFork className="w-3.5 h-3.5 text-blue-600" /> Automations & Workflows</>}
                {activeSideTool === "history" && <><Clock className="w-3.5 h-3.5 text-blue-600" /> Activity History</>}
                {activeSideTool === "calendar" && <><Calendar className="w-3.5 h-3.5 text-blue-600" /> Schedule Meeting</>}
                {activeSideTool === "documents" && <><File className="w-3.5 h-3.5 text-blue-600" /> Documents & Files</>}
                {activeSideTool === "payments" && <><DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Payment Link Generator</>}
                {activeSideTool === "ai" && <><Sparkles className="w-3.5 h-3.5 text-purple-600" /> AI Sales Copilot</>}
              </h3>
              <button
                onClick={() => setContactDetailsOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1"
                title="Hide side panel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {!activeConversation ? (
              <div className="py-16 text-center text-xs text-slate-400">
                No contact selected
              </div>
            ) : (
              <>
                {/* ── TOOL VIEW 1: CONTACT DETAILS ── */}
                {activeSideTool === "contact" && (
                  <div className="space-y-3.5">
                    {/* Contact Profile Summary */}
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs ${activeConversation.avatarBg}`}>
                        {activeConversation.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold text-slate-900 dark:text-white truncate block">
                          {activeConversation.name}
                        </span>
                        <span className="text-[10px] text-slate-400 block">{activeConversation.phone}</span>
                      </div>
                      <Link to="/contacts" title="Open contact in full CRM" className="text-slate-400 hover:text-blue-600">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    </div>

                    {/* Owner & Followers Dropdowns */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-[11px] text-slate-400 font-medium block mb-1">Owner</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="w-full flex items-center justify-between px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 text-[11px]">
                              <span className="truncate">{activeConversation.owner || "Unassigned"}</span>
                              <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-44">
                            <DropdownMenuItem onClick={() => handleAssignOwner("Unassigned")}>Unassigned</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAssignOwner("Master Admin")}>Master Admin</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAssignOwner("Sarah Connor (AI SDR)")}>Sarah Connor (AI SDR)</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAssignOwner("Marcus Vance")}>Marcus Vance</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div>
                        <span className="text-[11px] text-slate-400 font-medium block mb-1">Followers</span>
                        <button
                          onClick={() => toast.success(`Followers configured: ${activeConversation.followers || 1} team members`)}
                          className="w-full flex items-center justify-between px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 text-[11px]"
                        >
                          <span className="flex items-center gap-1 truncate">
                            <Users className="w-3 h-3 text-slate-400" /> {activeConversation.followers || 1} Followers
                          </span>
                          <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
                        </button>
                      </div>
                    </div>

                    {/* Tags Row with Add/Remove */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-slate-500 font-medium">Tags</span>
                        <button
                          onClick={() => setIsAddingTag(!isAddingTag)}
                          className="text-[10px] text-blue-600 hover:underline font-semibold"
                        >
                          {isAddingTag ? "Cancel" : "+ Add Tag"}
                        </button>
                      </div>

                      {isAddingTag && (
                        <form onSubmit={handleAddTag} className="flex gap-1">
                          <Input
                            size="sm"
                            value={newTagText}
                            onChange={(e) => setNewTagText(e.target.value)}
                            placeholder="Type tag & Enter..."
                            className="h-6 text-xs"
                            autoFocus
                          />
                          <Button size="sm" type="submit" className="h-6 px-2 text-[10px] bg-blue-600 text-white">Add</Button>
                        </form>
                      )}

                      <div className="flex flex-wrap gap-1">
                        {(activeConversation.tags || ["Lead", "High Intent"]).map(tag => (
                          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-700 dark:text-slate-300 font-medium">
                            {tag}
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              className="text-slate-400 hover:text-red-500"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Sub-tabs: All fields | DND | Actions */}
                    <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-md text-[11px] font-medium text-slate-600 dark:text-slate-300">
                      {["all-fields", "dnd", "actions"].map(st => (
                        <button
                          key={st}
                          onClick={() => setContactSubTab(st)}
                          className={`flex-1 py-1 text-center rounded capitalize transition-all ${
                            contactSubTab === st
                              ? "bg-white dark:bg-slate-900 shadow-2xs font-semibold text-slate-900 dark:text-white"
                              : "hover:text-slate-900"
                          }`}
                        >
                          {st.replace("-", " ")}
                        </button>
                      ))}
                    </div>

                    {/* Contact Edit Form */}
                    <div className="space-y-2.5 pt-1">
                      <div>
                        <label className="text-[11px] text-slate-500 font-medium block mb-1">First Name</label>
                        <Input
                          value={contactEdit.firstName}
                          onChange={(e) => setContactEdit(c => ({ ...c, firstName: e.target.value }))}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500 font-medium block mb-1">Last Name</label>
                        <Input
                          value={contactEdit.lastName}
                          onChange={(e) => setContactEdit(c => ({ ...c, lastName: e.target.value }))}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500 font-medium block mb-1">Email</label>
                        <Input
                          type="email"
                          value={contactEdit.email}
                          onChange={(e) => setContactEdit(c => ({ ...c, email: e.target.value }))}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-[11px] text-slate-500 font-medium">Phone</label>
                          <select
                            value={phoneType}
                            onChange={(e) => setPhoneType(e.target.value)}
                            className="text-[10px] bg-transparent text-slate-500 outline-none cursor-pointer"
                          >
                            <option value="Mobile">Mobile</option>
                            <option value="Work">Work</option>
                            <option value="Direct">Direct</option>
                          </select>
                        </div>
                        <Input
                          type="tel"
                          value={contactEdit.phone}
                          onChange={(e) => setContactEdit(c => ({ ...c, phone: e.target.value }))}
                          className="h-7 text-xs"
                        />
                      </div>

                      <label className="flex items-center gap-1.5 pt-1 text-[11px] text-slate-600 dark:text-slate-400 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={contactEdit.dnd}
                          onChange={(e) => setContactEdit(c => ({ ...c, dnd: e.target.checked }))}
                          className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5"
                        />
                        <span>Do Not Disturb (DND)</span>
                      </label>

                      <Button
                        size="sm"
                        onClick={handleSaveContact}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs h-7 mt-2"
                      >
                        Save Contact
                      </Button>
                    </div>
                  </div>
                )}

                {/* ── TOOL VIEW 2: NOTES ── */}
                {activeSideTool === "notes" && (
                  <div className="space-y-3">
                    <form onSubmit={handleAddNote} className="space-y-2">
                      <Textarea
                        value={newNoteInput}
                        onChange={(e) => setNewNoteInput(e.target.value)}
                        placeholder="Add a note about this contact..."
                        rows={3}
                        className="text-xs resize-none"
                      />
                      <Button size="sm" type="submit" disabled={!newNoteInput.trim()} className="w-full bg-blue-600 text-white text-xs h-7">
                        Add Note
                      </Button>
                    </form>

                    <div className="space-y-2 pt-2 divide-y divide-slate-100 dark:divide-slate-800">
                      {activeNotes.length === 0 ? (
                        <div className="text-center py-6 text-xs text-slate-400">No notes recorded yet.</div>
                      ) : (
                        activeNotes.map(n => (
                          <div key={n.id} className="pt-2 text-xs space-y-1">
                            <div className="flex items-center justify-between text-[10px] text-slate-400">
                              <span className="font-semibold text-slate-600 dark:text-slate-300">{n.author}</span>
                              <div className="flex items-center gap-2">
                                <span>{n.time}</span>
                                <button onClick={() => handleDeleteNote(n.id)} className="text-slate-300 hover:text-red-500">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <p className="text-slate-700 dark:text-slate-200">{n.text}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* ── TOOL VIEW 3: TASKS ── */}
                {activeSideTool === "tasks" && (
                  <div className="space-y-3">
                    <form onSubmit={handleAddTask} className="space-y-2 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                      <Input
                        value={newTaskInput}
                        onChange={(e) => setNewTaskInput(e.target.value)}
                        placeholder="New follow up task..."
                        className="h-7 text-xs"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={newTaskDueDate}
                          onChange={(e) => setNewTaskDueDate(e.target.value)}
                          placeholder="Due (e.g. Tomorrow)"
                          className="h-7 text-xs flex-1"
                        />
                        <Button size="sm" type="submit" disabled={!newTaskInput.trim()} className="h-7 px-3 bg-blue-600 text-white text-xs">
                          Add Task
                        </Button>
                      </div>
                    </form>

                    <div className="space-y-1.5 pt-1">
                      {activeTasks.length === 0 ? (
                        <div className="text-center py-6 text-xs text-slate-400">All tasks completed!</div>
                      ) : (
                        activeTasks.map(t => (
                          <div
                            key={t.id}
                            onClick={() => handleToggleTask(t.id)}
                            className={`p-2 rounded-lg border text-xs flex items-center justify-between gap-2 cursor-pointer transition-colors ${
                              t.completed
                                ? "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-400 line-through"
                                : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={t.completed}
                                onChange={() => handleToggleTask(t.id)}
                                className="rounded text-blue-600 w-3.5 h-3.5"
                              />
                              <span>{t.title}</span>
                            </div>
                            <span className="text-[10px] text-slate-400 shrink-0 font-medium">Due {t.dueDate}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* ── TOOL VIEW 4: WORKFLOWS ── */}
                {activeSideTool === "workflows" && (
                  <div className="space-y-3">
                    <p className="text-[11px] text-slate-500">Automated triggers and email/SMS drip campaigns enrolled for this contact.</p>
                    <div className="space-y-2">
                      {activeWorkflows.map(w => (
                        <div key={w.id} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-xs text-slate-900 dark:text-white truncate">{w.name}</div>
                            <div className="text-[10px] text-slate-400">{w.desc}</div>
                          </div>
                          <button
                            onClick={() => handleToggleWorkflow(w.id)}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${
                              w.active
                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                                : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                            }`}
                          >
                            {w.active ? "Enrolled" : "Paused"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── TOOL VIEW 5: HISTORY ── */}
                {activeSideTool === "history" && (
                  <div className="space-y-3">
                    <p className="text-[11px] text-slate-500">Timeline of omnichannel touches, opt-ins, and state changes.</p>
                    <div className="relative pl-4 space-y-4 border-l-2 border-slate-200 dark:border-slate-800 text-xs">
                      <div className="relative">
                        <div className="w-2 h-2 rounded-full bg-blue-600 absolute -left-[21px] top-1.5" />
                        <div className="font-semibold text-slate-800 dark:text-slate-100">SMS Outbound Sent</div>
                        <div className="text-[10px] text-slate-400">Today at 10:51 AM via Twilio Carrier</div>
                      </div>
                      <div className="relative">
                        <div className="w-2 h-2 rounded-full bg-purple-600 absolute -left-[21px] top-1.5" />
                        <div className="font-semibold text-slate-800 dark:text-slate-100">Assigned to Master Admin</div>
                        <div className="text-[10px] text-slate-400">Sep 25 by Automation Trigger</div>
                      </div>
                      <div className="relative">
                        <div className="w-2 h-2 rounded-full bg-emerald-600 absolute -left-[21px] top-1.5" />
                        <div className="font-semibold text-slate-800 dark:text-slate-100">Lead Captured from Sales Funnel</div>
                        <div className="text-[10px] text-slate-400">Sep 19 from "New Landing (RP)"</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── TOOL VIEW 6: CALENDAR MEETING ── */}
                {activeSideTool === "calendar" && (
                  <form onSubmit={handleSendMeetingInvite} className="space-y-3">
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium block mb-1">Meeting Title</label>
                      <Input
                        value={meetingForm.title}
                        onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] text-slate-500 font-medium block mb-1">Date</label>
                        <Input
                          value={meetingForm.date}
                          onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500 font-medium block mb-1">Time</label>
                        <Input
                          value={meetingForm.time}
                          onChange={(e) => setMeetingForm({ ...meetingForm, time: e.target.value })}
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium block mb-1">Channel / Location</label>
                      <select
                        value={meetingForm.channel}
                        onChange={(e) => setMeetingForm({ ...meetingForm, channel: e.target.value })}
                        className="w-full h-7 px-2 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 outline-none"
                      >
                        <option value="Google Meet">Google Meet</option>
                        <option value="Zoom Meeting">Zoom Meeting</option>
                        <option value="Phone Call">Phone Call</option>
                      </select>
                    </div>

                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 mt-2">
                      Schedule & Send Invite
                    </Button>
                  </form>
                )}

                {/* ── TOOL VIEW 7: DOCUMENTS ── */}
                {activeSideTool === "documents" && (
                  <div className="space-y-3">
                    <p className="text-[11px] text-slate-500">Share contracts, proposals, and invoices directly into the conversation.</p>
                    <div className="space-y-2">
                      {[
                        { name: "Website_Redesign_Proposal_v2.pdf", size: "2.4 MB" },
                        { name: "Service_Level_Agreement_Signed.pdf", size: "1.1 MB" },
                        { name: "Google_Review_Booster_Guide.pdf", size: "850 KB" }
                      ].map(d => (
                        <div key={d.name} className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                            <div className="truncate text-xs font-semibold">{d.name}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendDocument(d.name)}
                            className="h-6 px-2 text-[10px] text-blue-600 shrink-0"
                          >
                            Send
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── TOOL VIEW 8: PAYMENTS ── */}
                {activeSideTool === "payments" && (
                  <form onSubmit={handleSendPaymentLink} className="space-y-3">
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium block mb-1">Item / Service Description</label>
                      <Input
                        value={paymentForm.item}
                        onChange={(e) => setPaymentForm({ ...paymentForm, item: e.target.value })}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium block mb-1">Amount ($ USD)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        className="h-7 text-xs"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-8 mt-2">
                      Generate & Send Payment Link
                    </Button>
                  </form>
                )}

                {/* ── TOOL VIEW 9: AI SALES COPILOT ── */}
                {activeSideTool === "ai" && (
                  <div className="space-y-3">
                    <p className="text-[11px] text-slate-500">Autonomous objection handling and conversion recommendations powered by Gemini.</p>
                    <div className="space-y-2.5">
                      <div className="p-3 rounded-lg border border-purple-200 dark:border-purple-900 bg-purple-50/50 dark:bg-purple-950/20 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-purple-900 dark:text-purple-300">1. Handle Price Objection</span>
                          <Badge variant="outline" className="text-[9px] bg-purple-100 text-purple-800">High Match</Badge>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          "I completely understand budget is top of mind! We have a split $250/mo plan that lets us launch your site immediately. Would that work better for you?"
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReplyText("I completely understand budget is top of mind! We have a split $250/mo plan that lets us launch your site immediately. Would that work better for you?");
                            toast.success("Copied to reply box!");
                          }}
                          className="w-full h-6 text-xs text-purple-700 font-semibold"
                        >
                          Use in Reply →
                        </Button>
                      </div>

                      <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-blue-900 dark:text-blue-300">2. Request 10-Min Demo</span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          "Happy to show you the 3-step funnel in action over a quick 10-minute screen share. Does tomorrow at 2:00 PM work?"
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReplyText("Happy to show you the 3-step funnel in action over a quick 10-minute screen share. Does tomorrow at 2:00 PM work?");
                            toast.success("Copied to reply box!");
                          }}
                          className="w-full h-6 text-xs text-blue-700 font-semibold"
                        >
                          Use in Reply →
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Far-Right Quick-Action Toolbar (10 Vertical Icons matching screenshot) */}
          <div className="w-10 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col items-center py-3 gap-3.5 text-slate-500 select-none">
            {/* 1. Contact / Profile */}
            <button
              onClick={() => setActiveSideTool("contact")}
              title="Contact Profile"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "contact" ? "text-blue-600" : ""
              }`}
            >
              <User className="w-4 h-4" />
            </button>

            {/* 2. History */}
            <button
              onClick={() => setActiveSideTool("history")}
              title="Contact History"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "history" ? "text-blue-600" : ""
              }`}
            >
              <Clock className="w-4 h-4" />
            </button>

            {/* 3. Workflows */}
            <button
              onClick={() => setActiveSideTool("workflows")}
              title="Workflows"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "workflows" ? "text-blue-600" : ""
              }`}
            >
              <GitFork className="w-4 h-4" />
            </button>

            {/* 4. Notes */}
            <button
              onClick={() => setActiveSideTool("notes")}
              title="Notes"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "notes" ? "text-blue-600" : ""
              }`}
            >
              <FileText className="w-4 h-4" />
            </button>

            {/* 5. Tasks */}
            <button
              onClick={() => setActiveSideTool("tasks")}
              title="Tasks"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "tasks" ? "text-blue-600" : ""
              }`}
            >
              <CheckSquare className="w-4 h-4" />
            </button>

            {/* 6. Calendar */}
            <button
              onClick={() => setActiveSideTool("calendar")}
              title="Book Meeting"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "calendar" ? "text-blue-600" : ""
              }`}
            >
              <Calendar className="w-4 h-4" />
            </button>

            {/* 7. Documents */}
            <button
              onClick={() => setActiveSideTool("documents")}
              title="Documents & Files"
              className={`p-1 rounded hover:text-blue-600 transition-colors ${
                activeSideTool === "documents" ? "text-blue-600" : ""
              }`}
            >
              <File className="w-4 h-4" />
            </button>

            {/* 8. Payments */}
            <button
              onClick={() => setActiveSideTool("payments")}
              title="Invoice & Payment Link"
              className={`p-1 rounded hover:text-emerald-600 transition-colors ${
                activeSideTool === "payments" ? "text-emerald-600" : ""
              }`}
            >
              <DollarSign className="w-4 h-4" />
            </button>

            {/* 9. AI Assistant */}
            <button
              onClick={() => setActiveSideTool("ai")}
              title="AI Sales Copilot"
              className="relative p-1 rounded hover:text-purple-600 transition-colors"
            >
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                1
              </span>
            </button>
          </div>
        </section>
      )}

      {/* Button to reopen contact details if closed */}
      {!contactDetailsOpen && (
        <button
          onClick={() => setContactDetailsOpen(true)}
          className="absolute right-2 top-16 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-md p-1.5 rounded-full text-slate-500 hover:text-slate-800"
          title="Show Contact Details"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
      )}

      {/* ─────────────────────────────────────────────────────────────
          COMPOSE NEW EMAIL MODAL (MULTI-MAILBOX ENABLED)
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Mail className="w-5 h-5 text-blue-600" />
              Compose New Email Message
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Send an email directly through your merged corporate mailboxes with custom sender identity.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendComposeEmail} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">From Account Identity</Label>
              <div className="w-full mt-1 px-3 py-2 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{userMailbox.name}</span>
                  <span className="font-mono text-slate-500 text-[11px]">&lt;{userMailbox.email}&gt;</span>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                  <ShieldCheck className="w-3 h-3 text-emerald-500" /> Verified Account
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">To Email Address *</Label>
                <Input
                  type="email"
                  required
                  placeholder="recipient@company.com"
                  value={composeForm.toEmail}
                  onChange={(e) => setComposeForm({ ...composeForm, toEmail: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Recipient Full Name</Label>
                <Input
                  type="text"
                  placeholder="e.g. David Miller"
                  value={composeForm.toName}
                  onChange={(e) => setComposeForm({ ...composeForm, toName: e.target.value })}
                  className="text-xs mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Email Subject *</Label>
              <Input
                type="text"
                required
                placeholder="e.g. Proposal & Agreement for Q4 Scope"
                value={composeForm.subject}
                onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                className="text-xs mt-1 font-medium"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold">Message Body *</Label>
              <Textarea
                required
                rows={5}
                placeholder="Type your message here..."
                value={composeForm.body}
                onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                className="text-xs mt-1"
              />
            </div>

            {/* Signature Preview */}
            <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-700 dark:text-slate-300 block mb-0.5">Automated Signature Appended:</span>
              <pre className="whitespace-pre-wrap font-sans text-[10px] text-slate-500 dark:text-slate-400">
                {userMailbox.signature}
              </pre>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setComposeOpen(false)}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5 shadow-xs"
              >
                <Send className="w-3.5 h-3.5" /> Dispatch Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
